import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './MorningBoard.css';

// Morning board (2026-08-04) — the first thing the front desk sees.
//
// Misha: the overnight messages ARE the morning's work, and the
// secretary should not have to go looking for them. This is a
// chrome-free full-screen list meant to be opened automatically at
// Windows login (Startup-folder shortcut, browser app-mode) so it is
// simply THERE when she sits down. Anything deeper is one click into
// the dashboard — deliberately not rebuilt here.
//
// It is a VIEW, not a second source of truth: same API, same auth,
// same join to call_summaries as the dashboard block.

const asUtc = (s) => (s && !/Z|[+-]\d\d:\d\d$/.test(s) ? s + 'Z' : s);
const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const clock = (s) =>
  s ? new Date(asUtc(s)).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }) : '';
const dayLabel = (s) =>
  s ? new Date(asUtc(s)).toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'numeric' }) : '';

const parseTranscriptLines = (text) =>
  (text || '').split('\n').filter(l => l.trim()).map((line, i) => {
    const m = line.match(/^([^:]{1,12}):\s*(.*)$/);
    if (m && m[1] === 'מתקשר') return { key: i, role: 'caller', speaker: m[1], text: m[2] };
    if (m) return { key: i, role: 'agent', speaker: m[1], text: m[2] };
    return { key: i, role: 'agent', speaker: '', text: line };
  });

const REFRESH_MS = 60000;
const IDLE_HIDE_MS = 5 * 60 * 1000;

export default function MorningBoard() {
  const { authFetch } = useAuth();
  const [messages, setMessages] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [openId, setOpenId] = useState(null);
  // Option B (Misha, 2026-08-04): the screen stays signed in so it is
  // waiting when the secretary arrives — so the CONTENT must not be
  // readable from across the room. Metadata (who / when / number) is
  // visible because it is what she acts on; the message body and the
  // transcript are the sensitive part and stay behind a click.
  const [revealed, setRevealed] = useState(() => new Set());
  const [lastTouch, setLastTouch] = useState(() => Date.now());
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [mRes, sRes] = await Promise.allSettled([
        authFetch('/api/clinic/voice-messages?status=all'),
        authFetch('/api/summaries/call/recent?limit=50'),
      ]);
      if (mRes.status === 'fulfilled' && mRes.value.ok) {
        const data = await mRes.value.json();
        setMessages(Array.isArray(data) ? data : (data?.messages || []));
      }
      if (sRes.status === 'fulfilled' && sRes.value.ok) {
        const data = await sRes.value.json();
        const byId = {};
        (Array.isArray(data) ? data : []).forEach(s => {
          if (s.call_id) byId[s.call_id] = s;
        });
        setSummaries(byId);
      }
    } catch (e) { /* keep whatever is on screen */ }
    setLoaded(true);
  }, [authFetch]);

  // Auto-refresh: the window may sit open all day, and a message that
  // arrived after it opened must still show up without anyone
  // remembering to reload.
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const pending = useMemo(
    () => messages.filter(m => m.status !== 'handled'), [messages]);

  // Handled today stays visible-but-dimmed until midnight, then drops —
  // same rule as the dashboard block, keyed on handled_at (a 23:00
  // message handled at 09:15 must not vanish the moment it is touched).
  const shown = useMemo(() => {
    const today = fmtDate(new Date());
    const done = messages.filter(
      m => m.status === 'handled' && m.handled_at
        && fmtDate(new Date(asUtc(m.handled_at))) === today);
    return [...pending, ...done];
  }, [messages, pending]);

  // INSTALLABLE AS A REAL DESKTOP APP (2026-08-04). Linking a manifest
  // while this page is open is what makes Edge/Chrome offer "Install
  // this site as an app": the result is a Start-Menu entry with its own
  // icon and window, no address bar, its own taskbar identity — and
  // Edge exposes a per-app "start when you sign in" toggle, so it opens
  // by itself with no Startup-folder shortcut.
  //
  // It is still the web app underneath, which is the point: it updates
  // with every Pages deploy, needs no installer, no signing and no
  // update channel on a machine that has already lost its service stack
  // to a Windows update once.
  //
  // Scoped to THIS page deliberately — the manifest names the morning
  // board and starts at /morning, so installing from the dashboard is
  // not silently turned into installing this. Restored on unmount.
  //
  // NO service worker on purpose: offline caching would put patient
  // names, phone numbers and call transcripts on that disk. The app
  // should be blank when the network is gone, not quietly full of PHI.
  useEffect(() => {
    const existing = document.querySelector('link[rel="manifest"]');
    const prev = existing ? existing.getAttribute('href') : null;
    const link = existing || document.createElement('link');
    link.setAttribute('rel', 'manifest');
    link.setAttribute('href', '/morning.webmanifest');
    if (!existing) document.head.appendChild(link);
    return () => {
      if (prev !== null) link.setAttribute('href', prev);
      else link.remove();
    };
  }, []);

  // Re-hide everything after a few minutes untouched. Revealing is
  // deliberate; LEAVING it revealed is not — she gets called away
  // mid-message and the screen would otherwise sit open all afternoon.
  // Any interaction with the board resets the clock, so it never
  // collapses under someone who is still reading.
  useEffect(() => {
    if (revealed.size === 0) return undefined;
    const t = setInterval(() => {
      if (Date.now() - lastTouch > IDLE_HIDE_MS) {
        setRevealed(new Set());
        setOpenId(null);
      }
    }, 15000);
    return () => clearInterval(t);
  }, [revealed, lastTouch]);

  // The taskbar is the notification: the count is legible before the
  // window is even focused.
  useEffect(() => {
    document.title = pending.length
      ? `(${pending.length}) הודעות ממטופלים`
      : 'הודעות ממטופלים';
  }, [pending.length]);

  const markHandled = async (id) => {
    try {
      const r = await authFetch(`/api/clinic/voice-messages/${id}/handled`, { method: 'POST' });
      if (r.ok) {
        setMessages(prev => prev.map(
          x => x.id === id
            ? { ...x, status: 'handled', handled_at: new Date().toISOString() }
            : x));
      }
    } catch (e) { /* leave the row as pending on failure */ }
  };

  return (
    <div
      dir="rtl"
      className="morning-board"
      onClick={() => setLastTouch(Date.now())}
      onKeyDown={() => setLastTouch(Date.now())}
    >
      <header className="mb-header">
        <div>
          <h1>הודעות ממטופלים</h1>
          <p className="mb-sub">
            {pending.length === 0
              ? 'אין הודעות שממתינות לטיפול'
              : `${pending.length} ממתינות לטיפול`}
          </p>
        </div>
        <div className="mb-header-actions">
          {revealed.size > 0 && (
            <button
              className="mb-btn ghost"
              onClick={() => { setRevealed(new Set()); setOpenId(null); }}
            >
              הסתר הכול
            </button>
          )}
          <Link className="mb-dash-link" to="/">למסך המלא ←</Link>
        </div>
      </header>

      {!loaded ? (
        <p className="mb-empty">טוען…</p>
      ) : shown.length === 0 ? (
        <p className="mb-empty">הכול טופל. אין הודעות ממתינות.</p>
      ) : (
        <ul className="mb-list">
          {shown.map(m => {
            const cs = m.session_id ? summaries[m.session_id] : null;
            const transcript = cs?.transcription_full;
            const open = openId === m.id && transcript && revealed.has(m.id);
            const done = m.status === 'handled';
            const isRevealed = revealed.has(m.id);
            return (
              <li key={m.id} className={done ? 'mb-item done' : 'mb-item'}>
                <div className="mb-meta">
                  <span className="mb-when">
                    {dayLabel(m.created_at)} · {clock(m.created_at)}
                  </span>
                  <span className="mb-who">
                    {m.patient_name || 'לא מזוהה'}
                  </span>
                  {/* The number is ALWAYS shown and always dialable —
                      the point of this screen is the callback, and an
                      unidentified caller needs ringing back just as
                      much as a named one. (It used to render as plain
                      text whenever the name was missing, i.e. exactly
                      the case where the number is all you have.) */}
                  {m.caller_phone && (
                    <a className="mb-tel" href={`tel:${m.caller_phone}`}>
                      {m.caller_phone}
                    </a>
                  )}
                  {m.callback_requested && (
                    <span className="mb-callback">מבקש שיחזרו אליו</span>
                  )}
                </div>

                {isRevealed ? (
                  <p className="mb-text">{m.message}</p>
                ) : (
                  <button
                    className="mb-reveal"
                    onClick={() => setRevealed(prev => new Set(prev).add(m.id))}
                  >
                    הצג את ההודעה
                  </button>
                )}

                <div className="mb-actions">
                  {isRevealed && transcript && (
                    <button
                      className="mb-btn ghost"
                      onClick={() => setOpenId(open ? null : m.id)}
                    >
                      {open ? 'הסתר את השיחה' : 'הצג את השיחה המלאה'}
                    </button>
                  )}
                  {done ? (
                    <span className="mb-done">טופל ✓</span>
                  ) : (
                    <button className="mb-btn" onClick={() => markHandled(m.id)}>
                      סמן כטופל ✓
                    </button>
                  )}
                </div>

                {open && (
                  <div className="mb-transcript">
                    {parseTranscriptLines(transcript).map(t => (
                      <div key={t.key} className={`mb-turn ${t.role}`}>
                        <span className="mb-speaker">{t.speaker}</span>
                        {t.text}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
